import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Bell,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  CreditCard,
  Gavel,
  Gift,
  LucideIcon,
  Video,
  Wallet,
  X,
} from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import type { Notification } from '../types/notification';
import { NotificationIcon, notificationDisplay } from '../util/notificationDisplay';

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const ICONS: Record<NotificationIcon, LucideIcon> = {
  video: Video,
  'calendar-clock': CalendarClock,
  'calendar-check': CalendarCheck,
  'calendar-x': CalendarX,
  gavel: Gavel,
  wallet: Wallet,
  alert: AlertCircle,
  'credit-card': CreditCard,
  gift: Gift,
  bell: Bell,
};

const NotificationItem: React.FC<{
  notification: Notification;
  onRead: (id: string) => Promise<void>;
  onClose: () => void;
}> = ({ notification, onRead, onClose }) => {
  const display = notificationDisplay(notification);
  const Icon = ICONS[display.icon];

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent drawer from closing

    // An in-app destination means the <Link> is navigating now, so the drawer
    // has to close before anything is awaited or it would sit over the page.
    if (display.destination) {
      onClose();
    }

    if (!notification.read) {
      await onRead(notification.id);
    }

    // Handle navigation based on notification type
    if (notification.type === 'call_ready' && notification.data?.roomUrl) {
      window.open(notification.data.roomUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const className = `
        block w-full text-left p-4 border-b cursor-pointer transition-colors
        ${notification.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50 hover:bg-blue-100'}
      `;

  const body = (
    <div className="flex justify-between items-start gap-3">
      <span className="shrink-0 mt-0.5" data-testid={`notification-icon-${display.icon}`}>
        <Icon className="w-4 h-4 text-gray-500" />
      </span>
      <div className="min-w-0 flex-1">
        <h4 className="font-medium text-gray-900 break-words">{display.title}</h4>
        <p className="text-sm text-gray-600 break-words">{notification.message}</p>
        <span className="text-xs text-gray-500">
          {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
        </span>
      </div>
      {!notification.read && (
        <div className="shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2" />
      )}
    </div>
  );

  if (display.destination) {
    return (
      <Link to={display.destination} onClick={handleClick} className={className}>
        {body}
      </Link>
    );
  }

  return (
    <div onClick={handleClick} className={className}>
      {body}
    </div>
  );
};

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
  isOpen,
  onClose
}) => {
  const { notifications, markAsRead, markAllAsRead } = useNotifications();

  return (
    <>
    {isOpen && (
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={onClose}
      />
    )}
    <div
      className={`
        fixed z-50 inset-y-0 right-0 w-80 max-w-full bg-white shadow-lg transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}
    >
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="text-lg font-medium">Notifications</h3>
        <div className="flex items-center gap-2">
          {notifications.length > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="overflow-y-auto h-full pb-20">
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No notifications
          </div>
        ) : (
          notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={markAsRead}
              onClose={onClose}
            />
          ))
        )}
      </div>
    </div>
    </>
  );
};
